/**
 * `useRunJudges` — TanStack Query mutation for the
 * `POST /api/admin/content-versions/:contentVersionId/judges/run`
 * endpoint that powers the "Re-run judges" button on every
 * `ContentItemDetailPage` version slot.
 *
 * The server replaces prior `AiJudgeResult` rows + `AI_JUDGE`-sourced
 * `ContentFlag` rows for the version atomically (manually-raised flags
 * survive), so a successful re-run leaves the version in a clean state
 * with fresh scores. The mutation invalidates the ContentItem detail
 * query on success so every slot's judge strip re-renders with the new
 * DTOs without a manual refetch.
 *
 * Error handling lives at the call site (the button is the toast
 * surface): 429 `AI_BUDGET_EXCEEDED` and 429 `AI_RATE_LIMITED` come back
 * as `ApiClientError` with the matching `code`, so the slot can branch
 * on `err.code` to render distinct toast copy — mirroring the AI Studio
 * generate flow's pattern (`AiStudioPage.tsx`).
 *
 * Hook signature mirrors `useCreatePreviewToken`: takes the parent
 * `contentItemId` at construction so the success invalidation targets
 * the right detail key without the caller threading it through
 * `variables`.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { JudgeRunResponse } from "@ottopilot/shared";
import { apiPost } from "../lib/api";
import type { ApiClientError } from "../lib/api";
import { queryKeys } from "./queryKeys";

export function useRunJudges(contentItemId: string) {
  const qc = useQueryClient();
  return useMutation<JudgeRunResponse, ApiClientError, { contentVersionId: string }>({
    mutationFn: ({ contentVersionId }) =>
      apiPost<JudgeRunResponse>(`/api/admin/content-versions/${contentVersionId}/judges/run`, {}),
    onSuccess: () => {
      // The detail query carries the `judges` array on every version
      // slot — refetch so all three slot views (Published / Draft /
      // Archived rows) pick up the fresh DTOs in one round-trip.
      void qc.invalidateQueries({ queryKey: queryKeys.contentItems.detail(contentItemId) });
      // A re-run also writes new AiGeneration rows that count against
      // the monthly budget — invalidate the AI cache so the budget
      // banner picks up the new spend on its next poll. Mirrors the
      // AI Studio generate mutation.
      void qc.invalidateQueries({ queryKey: queryKeys.ai.all });
    },
  });
}
